import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TestimonialsSection = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState([]);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/testimonials`);
        if (r.data.testimonials?.length > 0) setItems(r.data.testimonials);
      } catch {}
    };
    load();
  }, []);

  const goNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % (items.length || 1));
  }, [items.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + (items.length || 1)) % (items.length || 1));
  }, [items.length]);

  // Determine if current item is a video
  const current = items[currentIndex] || null;
  const isVideo = current && (current.media_type === 'video' || (current.media_url || '').match(/\.(mp4|mov|webm|avi|mkv)$/i));

  // Auto-advance timer: only for images (8s), videos use onEnded
  useEffect(() => {
    if (items.length === 0) return;
    if (timerRef.current) clearInterval(timerRef.current);

    if (!isVideo) {
      timerRef.current = setInterval(goNext, 8000);
    }
    // If video, no timer — onEnded handles it

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [currentIndex, items.length, isVideo, goNext]);

  const handleVideoEnded = () => {
    goNext();
  };

  if (items.length === 0 || !current) return null;

  const text = isZh ? (current.text_zh || current.text_en) : current.text_en;

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('testimonials.title')}</h2>
          <p className="text-xl text-gray-600">{t('testimonials.subtitle')}</p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          <Card className="overflow-hidden shadow-2xl border-0">
            <div className="grid md:grid-cols-2">
              {/* Media Side */}
              <div className="h-64 md:h-auto relative overflow-hidden bg-black min-h-[280px]">
                {current.video_link ? (
                  <a href={current.video_link} target="_blank" rel="noopener noreferrer" className="block w-full h-full relative cursor-pointer">
                    <img src={current.media_url} alt={current.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center hover:bg-black/40 transition-colors">
                      <svg className="w-16 h-16 drop-shadow-lg" viewBox="0 0 64 64" fill="none"><polygon points="24,14 24,50 50,32" fill="rgba(255,255,255,0.85)" /></svg>
                    </div>
                  </a>
                ) : isVideo ? (
                  <video
                    ref={videoRef}
                    key={`video-${current.id}-${currentIndex}`}
                    src={current.media_url}
                    controls
                    autoPlay
                    playsInline
                    onEnded={handleVideoEnded}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <>
                    <img src={current.media_url} alt={current.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </>
                )}
              </div>

              {/* Text Side */}
              <CardContent className="p-8 md:p-12 flex flex-col justify-center bg-gradient-to-br from-white to-red-50">
                <div className="flex mb-4">
                  {[...Array(current.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-red-600 text-red-600" />
                  ))}
                </div>
                <p className="text-gray-700 text-lg leading-relaxed mb-6 italic">&ldquo;{text}&rdquo;</p>
                <div>
                  <p className="font-bold text-xl text-gray-900">{current.name}</p>
                  <p className="text-gray-600">{current.location}</p>
                </div>
                <div className="flex space-x-2 mt-8">
                  {items.map((_, index) => (
                    <button key={index} onClick={() => setCurrentIndex(index)} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'w-8 bg-red-600' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} />
                  ))}
                </div>
              </CardContent>
            </div>
          </Card>

          <button onClick={goPrev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white rounded-full p-3 shadow-lg hover:bg-red-600 hover:text-white transition-all duration-300 hover:scale-110">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={goNext} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white rounded-full p-3 shadow-lg hover:bg-red-600 hover:text-white transition-all duration-300 hover:scale-110">
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="p-6 bg-white rounded-xl shadow-md"><div className="text-4xl font-bold text-red-600 mb-2">1500+</div><div className="text-gray-700 font-medium">{t('testimonials.projects')}</div></div>
          <div className="p-6 bg-white rounded-xl shadow-md"><div className="text-4xl font-bold text-red-600 mb-2">98%</div><div className="text-gray-700 font-medium">{t('testimonials.satisfaction')}</div></div>
          <div className="p-6 bg-white rounded-xl shadow-md"><div className="text-4xl font-bold text-red-600 mb-2">5.0</div><div className="text-gray-700 font-medium">{t('testimonials.rating')}</div></div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
